import { useState } from "react"
import { api } from "@/shared/api-client"

const MESSAGES: Record<string, string> = {
  invalid_login: "Логин: 3–32 символа, буквы, цифры, точка, дефис или подчёркивание, без пробелов.",
  weak_password: "Пароль слишком простой: нужно не меньше 12 символов, и он не должен совпадать с логином.",
  password_must_differ: "Новый пароль должен отличаться от прежнего.",
  current_password_wrong: "Текущий (временный) пароль введён неверно.",
  login_taken: "Такой логин уже занят. Выберите другой.",
  credentials_change_required: "Сначала нужно выбрать свой логин и пароль.",
  approval_required: "Нужно подтверждение владельца.",
}

interface Props {
  /** Для первого входа: временный пароль уже введён и подставляется сам. */
  currentPassword?: string
  submitLabel: string
  onDone: () => void
}

/**
 * Jarvis: Форма «выберите свой логин и пароль».
 * Первый вход (временные данные) и добровольная смена в профиле. Для владельца сервер просит
 * подтверждение кодом Google Authenticator — окно подтверждения открывается само (api-client).
 */
export function CredentialsForm({ currentPassword, submitLabel, onDone }: Props) {
  const [cur, setCur] = useState(currentPassword ?? "")
  const [login, setLogin] = useState("")
  const [pw, setPw] = useState("")
  const [pw2, setPw2] = useState("")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (pw !== pw2) {
      setError("Пароли не совпадают.")
      return
    }
    setBusy(true)
    try {
      await api.post("/account/credentials", { current_password: cur, new_login: login.trim(), new_password: pw })
      onDone()
    } catch (err) {
      const m = err instanceof Error ? err.message : ""
      setError(MESSAGES[m] ?? (m || "Не удалось сохранить."))
    } finally {
      setBusy(false)
    }
  }

  const input =
    "w-full px-3.5 py-2.5 rounded-lg bg-white/[5%] border border-white/[8%] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/60"
  const label = "block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wide"
  return (
    <form onSubmit={submit} className="space-y-4">
      {currentPassword === undefined && (
        <div>
          <label className={label}>Текущий пароль</label>
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={input} required autoComplete="current-password" />
        </div>
      )}
      <div>
        <label className={label}>Новый логин</label>
        <input value={login} onChange={(e) => setLogin(e.target.value)} className={input} required autoFocus autoComplete="username" placeholder="например, misha" />
      </div>
      <div>
        <label className={label}>Новый пароль (от 12 символов)</label>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={input} required minLength={12} autoComplete="new-password" />
      </div>
      <div>
        <label className={label}>Повторите пароль</label>
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={input} required minLength={12} autoComplete="new-password" />
      </div>
      {error && (
        <p className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-[var(--hh-accent-from)] to-[var(--hh-accent-to)] hover:brightness-110 text-white text-sm font-medium disabled:opacity-60"
      >
        {busy ? "Сохраняю…" : submitLabel}
      </button>
    </form>
  )
}
